import React, { useState, useEffect, useCallback, useRef } from "react";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const ink = "#0A0A0F";
const accent = "#0033FF";
const muted = "rgba(10,10,15,0.58)";
const green = "#22c55e";
const amber = "#f59e0b";
const danger = "#ef4444";

const money = (v, cur) => (v || v === 0 ? `${cur && cur !== "USD" ? cur + " " : "$"}${Number(v).toFixed(2)}` : "—");

const STATUS_META = {
  draft: { label: "Draft", color: amber },
  creating: { label: "Building…", color: amber },
  sent: { label: "Sent to Reverb", color: accent },
  published: { label: "Live", color: green },
  listed: { label: "Listed", color: green },
  failed: { label: "Failed", color: danger },
  none: { label: "—", color: muted },
};

const mono = "'JetBrains Mono', monospace";

// Common eBay variation axis names (free-text allowed; Size is the default)
const EBAY_VAR_NAMES = ["Size", "Color", "Style", "Material", "Length", "Model", "Type"];

// Small labeled input to set the eBay variation axis name for multi-variant listings.
function EbayVariationName({ value, onChange }) {
  return (
    <div style={{ flex: "0 1 150px", minWidth: 120 }}>
      <label style={{ ...S.label, marginBottom: 4 }}>eBay variation label</label>
      <input
        list="ebay-var-names"
        value={value || "Size"}
        onChange={(e) => onChange(e.target.value)}
        title="What the variations differ by on eBay (Size, Color, etc.)"
        style={{ ...S.input, fontSize: 12, padding: "6px 8px" }}
      />
      <datalist id="ebay-var-names">{EBAY_VAR_NAMES.map((n) => <option key={n} value={n} />)}</datalist>
    </div>
  );
}

// SKU shown in the table: single → the SKU; multiple variants → first + "+N".
const skuDisplay = (r) => {
  const list = (r.skus && r.skus.length ? r.skus : (r.sku ? [r.sku] : []));
  if (!list.length) return "—";
  return list.length > 1 ? `${list[0]} +${list.length - 1}` : list[0];
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.none;
  return <span style={{ ...S.typeBadge, color: m.color, borderColor: m.color, fontSize: 11 }}>{m.label}</span>;
}

const CONFIG_FIELDS = [
  { section: "eBay", key: "ebay_category_id", label: "Default Category ID", hint: "fallback leaf categoryId — used only when a draft has no category picked on its card" },
  { section: "eBay", key: "ebay_payment_policy_id", label: "Payment Policy ID" },
  { section: "eBay", key: "ebay_return_policy_id", label: "Return Policy ID" },
  { section: "eBay", key: "ebay_fulfillment_policy_id", label: "Fulfillment Policy ID" },
  { section: "eBay", key: "ebay_location_key", label: "Merchant Location Key" },
  { section: "eBay", key: "ebay_condition", label: "Condition", hint: "default NEW" },
  { section: "eBay", key: "ebay_brand", label: "Brand", hint: "item specific" },
  { section: "eBay", key: "ebay_country", label: "Country of Manufacture", hint: "item specific" },
  { section: "eBay", key: "ebay_price_mult", label: "Price multiplier", hint: "1 = match Etsy" },
  { section: "Reverb", key: "reverb_make", label: "Make / Brand" },
  { section: "Reverb", key: "reverb_category_uuid", label: "Default category (fallback)", hint: "used only if a listing has no category set in the Migrate tab" },
  { section: "Reverb", key: "reverb_condition_uuid", label: "Condition", hint: "e.g. Brand New" },
  { section: "Reverb", key: "reverb_inventory", label: "Default inventory per listing", hint: "blank = use Etsy stock; e.g. 999 for made-to-order" },
  { section: "Reverb", key: "reverb_shipping_mode", label: "Shipping mode", selectOptions: [
    { value: "calculated", label: "Calculated — Reverb prices by weight + buyer location" },
    { value: "flat", label: "Flat US rate" },
    { value: "free", label: "Free shipping" },
    { value: "profile", label: "Use a Reverb shipping profile" },
  ] },
  { section: "Reverb", key: "reverb_shipping_profile_id", label: "Shipping Profile", hint: "only used in 'profile' mode" },
  { section: "Reverb", key: "reverb_shipping_rate", label: "Flat US Shipping Rate", hint: "only used in 'flat' mode; e.g. 6.00" },
  { section: "Reverb", key: "reverb_price_mult", label: "Price multiplier", hint: "1 = match Etsy" },
  { section: "Reverb", key: "reverb_bump_rate", label: "Bump rate", hint: "0.05 = 5% promo · 0 = off" },
  { section: "Reverb", key: "reverb_country", label: "Country of origin", hint: "ISO code, e.g. US" },
  { section: "Reverb", key: "reverb_year", label: "Default year", hint: "per-listing on each card" },
  { section: "Reverb", key: "reverb_finish", label: "Default finish", hint: "per-listing on each card" },
];

export default function ListingMigratorPage({ store }) {
  const storeKey = store === "designshop" ? "designshop" : "vibratovault";
  const storeLabel = storeKey === "designshop" ? "Design Shop" : "Vibrato Vault";

  const [tab, setTab] = useState("migrate");
  const [listings, setListings] = useState([]);
  const [selections, setSelections] = useState({}); // id -> { ebay, reverb }
  const [drafts, setDrafts] = useState([]);
  const [config, setConfig] = useState({});
  const [reverbTax, setReverbTax] = useState({ categories: [], conditions: [], shippingProfiles: [] });
  const [ebayAccount, setEbayAccount] = useState({ paymentPolicies: [], returnPolicies: [], fulfillmentPolicies: [], locations: [], accountType: "" });
  const [etsyConnected, setEtsyConnected] = useState(false);

  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [refreshingListed, setRefreshingListed] = useState(false);
  const [building, setBuilding] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [hideMigrated, setHideMigrated] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [bulkCat, setBulkCat] = useState("");
  // Friendly labels for picked eBay category IDs (we only store the ID server-side).
  // Persisted so the path text survives reloads.
  const [ebayCatLabels, setEbayCatLabels] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ebay-cat-labels") || "{}"); } catch { return {}; }
  });
  const rememberEbayCat = useCallback((id, path) => {
    if (!id || !path) return;
    setEbayCatLabels((prev) => {
      const next = { ...prev, [id]: path };
      try { localStorage.setItem("ebay-cat-labels", JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);
  // Favorite eBay categories — starred for quick reuse across every picker.
  const [ebayCatFavs, setEbayCatFavs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ebay-cat-favs") || "[]"); } catch { return []; }
  });
  const toggleEbayCatFav = useCallback((cat) => {
    if (!cat || !cat.id) return;
    setEbayCatFavs((prev) => {
      const exists = prev.some((f) => f.id === cat.id);
      const next = exists
        ? prev.filter((f) => f.id !== cat.id)
        : [{ id: cat.id, name: cat.name || `#${cat.id}`, path: cat.path || "" }, ...prev];
      try { localStorage.setItem("ebay-cat-favs", JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");

  // CEO save form for settings
  const [ceo, setCeo] = useState({ empId: "", password: "" });
  const [savingCfg, setSavingCfg] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const q = `?store=${storeKey}`;

  const fetchEtsy = useCallback(() => {
    fetch(`${API_URL}/api/etsy/settings${q}`)
      .then((r) => r.json())
      .then((d) => setEtsyConnected(!!d.connected))
      .catch(() => {});
  }, [q]);

  const fetchSource = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/api/crosslist/source${q}`)
      .then((r) => r.json())
      .then((d) => {
        const rows = d.listings || [];
        // newest / never-migrated first
        rows.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
        setListings(rows);
        setSelections((prev) => {
          const next = { ...prev };
          rows.forEach((r) => {
            if (!next[r.etsyListingId]) {
              // Default everything deselected — the user picks each platform manually.
              next[r.etsyListingId] = { ebay: false, reverb: false };
            }
          });
          return next;
        });
      })
      .catch(() => setError("Could not load listings"))
      .finally(() => setLoading(false));
  }, [q]);

  const fetchDrafts = useCallback(() => {
    fetch(`${API_URL}/api/crosslist/drafts${q}`)
      .then((r) => r.json())
      .then((d) => setDrafts(d.drafts || []))
      .catch(() => {});
  }, [q]);

  const fetchConfig = useCallback(() => {
    fetch(`${API_URL}/api/crosslist/config`)
      .then((r) => r.json())
      .then((d) => setConfig(d.config || {}))
      .catch(() => {});
  }, []);

  const fetchEbayAccount = useCallback(() => {
    fetch(`${API_URL}/api/crosslist/ebay-account${q}`)
      .then((r) => r.json())
      .then((d) => setEbayAccount({
        paymentPolicies: d.paymentPolicies || [], returnPolicies: d.returnPolicies || [],
        fulfillmentPolicies: d.fulfillmentPolicies || [], locations: d.locations || [],
        accountType: d.accountType || "",
      }))
      .catch(() => {});
  }, [q]);

  const fetchReverbTax = useCallback(() => {
    fetch(`${API_URL}/api/crosslist/reverb-taxonomy`)
      .then((r) => r.json())
      .then((d) => setReverbTax({
        categories: (d.categories || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        conditions: d.conditions || [],
        shippingProfiles: d.shippingProfiles || [],
      }))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchEtsy(); fetchSource(); fetchDrafts(); fetchConfig(); fetchReverbTax(); fetchEbayAccount(); }, [fetchEtsy, fetchSource, fetchDrafts, fetchConfig, fetchReverbTax, fetchEbayAccount]);
  // When opening Review, silently reconcile with Reverb so anything published there
  // moves to the "Live" group on its own.
  useEffect(() => {
    if (tab !== "review") return;
    fetch(`${API_URL}/api/crosslist/refresh-reverb-status${q}`, { method: "POST" })
      .then((r) => r.json()).then((d) => { if (!d.error) fetchDrafts(); }).catch(() => {});
  }, [tab, q, fetchDrafts]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(""), 4000); return () => clearTimeout(t); }, [toast]);

  const syncPoll = useRef(null);

  const pollSyncStatus = useCallback(() => {
    if (syncPoll.current) clearInterval(syncPoll.current);
    syncPoll.current = setInterval(() => {
      fetch(`${API_URL}/api/crosslist/sync-status${q}`)
        .then((r) => r.json())
        .then((d) => {
          const s = d.status || "";
          if (s.startsWith("done")) {
            clearInterval(syncPoll.current); syncPoll.current = null;
            setSyncing(false); setToast(`Synced from Etsy · ${s.slice(5)}`); fetchSource();
          } else if (s.startsWith("error")) {
            clearInterval(syncPoll.current); syncPoll.current = null;
            setSyncing(false); setError(`Sync error: ${s.slice(6)}`);
          }
        })
        .catch(() => {});
    }, 4000);
  }, [q, fetchSource]);

  // Resume the spinner if a sync is already running (e.g. after a page refresh).
  useEffect(() => {
    fetch(`${API_URL}/api/crosslist/sync-status${q}`)
      .then((r) => r.json())
      .then((d) => { if ((d.status || "").startsWith("running")) { setSyncing(true); pollSyncStatus(); } })
      .catch(() => {});
    return () => { if (syncPoll.current) clearInterval(syncPoll.current); };
  }, [q, pollSyncStatus]);

  const syncFromEtsy = () => {
    setSyncing(true); setError("");
    fetch(`${API_URL}/api/crosslist/sync-source${q}`, { method: "POST" })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) { setError(d.error); setSyncing(false); return; }
        setToast("Syncing from Etsy in the background — this can take a minute…");
        pollSyncStatus();
      })
      .catch(() => { setError("Sync failed"); setSyncing(false); });
  };

  const refreshListed = () => {
    setRefreshingListed(true); setError("");
    fetch(`${API_URL}/api/crosslist/refresh-listed${q}`, { method: "POST" })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) { setError(d.error); return; }
        const checked = [d.ebayChecked ? "eBay" : null, d.reverbChecked ? "Reverb" : null].filter(Boolean).join(" + ");
        setToast(checked ? `Re-checked ${checked} for existing listings` : "No platform connected to check");
        fetchSource();
      })
      .catch(() => setError("Refresh failed"))
      .finally(() => setRefreshingListed(false));
  };

  const toggle = (id, platform) => {
    setSelections((prev) => ({ ...prev, [id]: { ...prev[id], [platform]: !prev[id]?.[platform] } }));
  };

  // eBay variant gap: is the listing live on eBay with variant data, and are some
  // Etsy variants NOT on that listing? (Only trust it once we know eBay's variant set.)
  const ebayGap = (r) => {
    const liveEbay = ["published", "listed"].includes(r.ebayStatus);
    const vars = r.variations || [];
    if (!liveEbay || !r.ebayVariantsKnown) return { warn: false, missing: [], includedMissing: [] };
    const excluded = new Set(r.excludedVariations || []);
    const missing = vars.filter((v) => !v.listedEbay);
    const includedMissing = missing.filter((v) => !excluded.has(v.label));
    return { warn: missing.length > 0, missing, includedMissing };
  };

  const [revising, setRevising] = useState("");
  const reviseEbay = (lid, allowRelist = false) => {
    if (allowRelist && !window.confirm(
      "The current eBay listing is a single item, so eBay can't add a variation to it.\n\nThis will END that listing and create one new variation listing containing all checked variants. The old listing's URL, watchers and sales history will be lost.\n\nContinue?"
    )) return;
    setRevising(lid); setError("");
    fetch(`${API_URL}/api/crosslist/ebay-revise`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ store: storeKey, etsyListingId: lid, allowRelist }),
    }).then((r) => r.json())
      .then((d) => {
        if (d.error) { setError(d.error); return; }
        setToast(d.mode === "relisted"
          ? `Relisted on eBay as one variation listing · ${d.variantsNow} variants`
          : `eBay listing updated · ${d.variantsNow} variants`);
        fetchSource();
      })
      .catch(() => setError("Couldn't update eBay listing"))
      .finally(() => setRevising(""));
  };

  const updateSource = (etsyListingId, fields) => {
    setListings((prev) => prev.map((r) => (r.etsyListingId === etsyListingId ? { ...r, ...fields } : r)));
    setDrafts((prev) => prev.map((d) => (d.etsyListingId === etsyListingId ? { ...d, ...fields } : d)));
    fetch(`${API_URL}/api/crosslist/update-source`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ store: storeKey, etsyListingId, ...fields }),
    }).then((r) => r.json()).catch(() => setError("Couldn't save"));
  };

  const updateDraft = (id, fields) => {
    setDrafts((prev) => prev.map((x) => (x.id === id ? { ...x, ...fields, model: fields.titleOverride ? fields.titleOverride : x.model } : x)));
    fetch(`${API_URL}/api/crosslist/update-draft`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ store: storeKey, id, ...fields }),
    }).then((r) => r.json()).then(() => fetchDrafts()).catch(() => setError("Couldn't save title"));
  };

  const toggleVariation = (r, label) => {
    const ex = new Set(r.excludedVariations || []);
    if (ex.has(label)) ex.delete(label); else ex.add(label);
    const arr = [...ex];
    setListings((prev) => prev.map((x) => (x.etsyListingId === r.etsyListingId ? { ...x, excludedVariations: arr } : x)));
    fetch(`${API_URL}/api/crosslist/update-source`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ store: storeKey, etsyListingId: r.etsyListingId, excludedVariations: arr }),
    }).then((rr) => rr.json()).catch(() => setError("Couldn't save"));
  };

  const assignCategory = (ids, uuid) => {
    if (!ids.length) return;
    setListings((prev) => prev.map((r) => (ids.includes(r.etsyListingId) ? { ...r, reverbCategoryUuid: uuid } : r)));
    setDrafts((prev) => prev.map((d) => (ids.includes(d.etsyListingId) ? { ...d, reverbCategoryUuid: uuid } : d)));
    fetch(`${API_URL}/api/crosslist/assign-category`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ store: storeKey, etsyListingIds: ids, reverbCategoryUuid: uuid }),
    }).then((r) => r.json()).catch(() => setError("Couldn't save category"));
  };

  const setAll = (platform, value) => {
    setSelections((prev) => {
      const next = { ...prev };
      filtered.forEach((r) => { next[r.etsyListingId] = { ...next[r.etsyListingId], [platform]: value }; });
      return next;
    });
  };

  const buildDrafts = () => {
    const items = listings
      .map((r) => ({ etsyListingId: r.etsyListingId, ebay: !!selections[r.etsyListingId]?.ebay, reverb: !!selections[r.etsyListingId]?.reverb }))
      .filter((it) => it.ebay || it.reverb);
    if (!items.length) { setError("Nothing checked — pick at least one listing/platform"); return; }
    setBuilding(true); setError("");
    fetch(`${API_URL}/api/crosslist/build-drafts`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ store: storeKey, items }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) { setError(d.error); return; }
        setToast(`Built ${d.drafted} draft(s) · ${d.failed} failed · ${d.skipped} already existed`);
        fetchSource(); fetchDrafts(); setTab("review");
      })
      .catch(() => setError("Build failed"))
      .finally(() => setBuilding(false));
  };

  const rebuildOne = (d) => {
    setBuilding(true); setError("");
    fetch(`${API_URL}/api/crosslist/build-drafts`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ store: storeKey, items: [{ etsyListingId: d.etsyListingId, ebay: d.platform === "ebay", reverb: d.platform === "reverb" }] }),
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.error) { setError(res.error); return; }
        setToast(`Rebuilt: ${res.drafted} draft · ${res.failed} failed`);
        fetchDrafts(); fetchSource();
      })
      .catch(() => setError("Rebuild failed"))
      .finally(() => setBuilding(false));
  };

  const publish = (ids) => {
    if (!ids.length) return;
    setPublishing(true); setError("");
    fetch(`${API_URL}/api/crosslist/publish`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ store: storeKey, ids }),
    })
      .then((r) => r.json())
      .then((d) => {
        const results = d.results || [];
        const ok = results.filter((x) => x.status === "published").length;
        const failed = results.filter((x) => x.status === "failed").length;
        const manual = results.filter((x) => x.manual).length;
        const parts = [];
        if (ok) parts.push(`${ok} published`);
        if (manual) parts.push(`${manual} synced to Reverb drafts (finish + publish there)`);
        if (failed) parts.push(`${failed} failed`);
        setToast(parts.join(" · ") || "Nothing to publish");
        fetchDrafts(); fetchSource();
      })
      .catch(() => setError("Publish failed"))
      .finally(() => setPublishing(false));
  };

  const checkReverbStatus = () => {
    setCheckingStatus(true); setError("");
    fetch(`${API_URL}/api/crosslist/refresh-reverb-status${q}`, { method: "POST" })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) { setError(d.error); return; }
        setToast(`Checked Reverb: ${d.updated} now reflect their live status`);
        fetchDrafts(); fetchSource();
      })
      .catch(() => setError("Reverb status check failed"))
      .finally(() => setCheckingStatus(false));
  };

  const deleteDraft = (id) => {
    fetch(`${API_URL}/api/crosslist/drafts/${id}${q}`, { method: "DELETE" })
      .then((r) => r.json())
      .then(() => { fetchDrafts(); fetchSource(); })
      .catch(() => {});
  };

  const saveConfig = () => {
    if (!ceo.empId || !ceo.password) { setError("CEO ID + password required to save settings"); return; }
    setSavingCfg(true); setError("");
    fetch(`${API_URL}/api/crosslist/config`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ empId: ceo.empId, password: ceo.password, config }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) { setError(d.error); return; }
        setToast("Settings saved"); setCeo({ empId: "", password: "" });
      })
      .catch(() => setError("Save failed"))
      .finally(() => setSavingCfg(false));
  };

  const s = search.trim().toLowerCase();
  const filtered = listings.filter((r) => {
    if (hideMigrated && r.migratedEverywhere) return false;
    if (s && !(r.title || "").toLowerCase().includes(s)) return false;
    return true;
  });

  const draftReady = drafts.filter((d) => d.status === "draft").map((d) => d.id);
  const cols = [
    { key: "draft", label: "Drafts — configure, then Publish to send", statuses: ["draft", "creating", "failed"] },
    { key: "sent", label: "Sent to Reverb — finish dimensions & publish there", statuses: ["sent"] },
    { key: "published", label: "Published", statuses: ["published"] },
  ];

  const tabs = [
    { key: "migrate", label: "Migrate" },
    { key: "review", label: `Review & Publish${drafts.length ? ` (${drafts.length})` : ""}` },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: ink, letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1 }}>Listing Migrator</h2>
          <div style={{ fontSize: 13, color: muted, marginTop: 2 }}>Etsy → eBay + Reverb · {storeLabel}</div>
        </div>
        {tab === "migrate" && (
          <button onClick={syncFromEtsy} disabled={syncing || !etsyConnected} style={{ ...S.addBtn, opacity: (syncing || !etsyConnected) ? 0.5 : 1, cursor: (syncing || !etsyConnected) ? "not-allowed" : "pointer" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
            {syncing ? "Syncing…" : "Sync from Etsy"}
          </button>
        )}
      </div>

      <div style={{ ...S.nav, marginBottom: 20 }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => { setTab(t.key); setSearch(""); }} style={{ ...S.navBtn, ...(tab === t.key ? S.navBtnActive : {}) }}>
            {t.label}
          </button>
        ))}
      </div>

      {toast && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "rgba(34,197,94,0.12)", border: `1px solid ${green}`, fontSize: 14, color: "#0c3a2a", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{toast}</span>
          <button onClick={() => setToast("")} style={{ background: "none", border: "none", color: "#0c3a2a", cursor: "pointer", fontSize: 16 }}>x</button>
        </div>
      )}
      {error && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: `1px solid ${danger}`, fontSize: 14, color: danger, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{error}</span>
          <button onClick={() => setError("")} style={{ background: "none", border: "none", color: danger, cursor: "pointer", fontSize: 16 }}>x</button>
        </div>
      )}

      {!etsyConnected && tab === "migrate" && (
        <div style={{ marginBottom: 16, padding: "12px 16px", background: "rgba(245,158,11,0.1)", border: `1px solid ${amber}`, fontSize: 14, color: ink }}>
          Etsy isn't connected for {storeLabel} (or the connection lacks the <strong>listings_r</strong> scope). Connect/re-authorize on the <strong>Etsy</strong> page first, then come back and sync.
        </div>
      )}

      {tab === "migrate" && (
        <>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search listings…" style={{ ...S.input, maxWidth: 280 }} />
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", color: ink }}>
              <input type="checkbox" checked={hideMigrated} onChange={(e) => setHideMigrated(e.target.checked)} /> Hide migrated
            </label>
            <div style={{ flex: 1 }} />
            <button onClick={refreshListed} disabled={refreshingListed} style={{ ...S.clearBtn, fontSize: 13, opacity: refreshingListed ? 0.6 : 1 }} title="Re-check eBay & Reverb for products already listed">
              {refreshingListed ? "Checking…" : "Refresh listed status"}
            </button>
            <button onClick={buildDrafts} disabled={building} style={{ ...S.addBtn, opacity: building ? 0.6 : 1, cursor: building ? "wait" : "pointer" }}>
              {building ? "Building…" : "Build Drafts"}
            </button>
          </div>

          {filtered.length === 0 ? (
            <div style={{ padding: "40px 20px", textAlign: "center", color: muted, border: `2px dashed ${ink}` }}>
              {listings.length === 0 ? "No listings yet — click “Sync from Etsy” to pull your shop." : "No listings match the current filter."}
            </div>
          ) : (
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Listing</th>
                    <th style={S.th}>SKU</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Price</th>
                    <th style={{ ...S.th, textAlign: "center" }}>
                      <div>eBay</div>
                      <div style={{ display: "flex", gap: 4, justifyContent: "center", marginTop: 4 }}>
                        <button onClick={() => setAll("ebay", true)} style={linkBtn}>all</button>
                        <span style={{ color: muted }}>/</span>
                        <button onClick={() => setAll("ebay", false)} style={linkBtn}>none</button>
                      </div>
                    </th>
                    <th style={{ ...S.th, textAlign: "center" }}>
                      <div>Reverb</div>
                      <div style={{ display: "flex", gap: 4, justifyContent: "center", marginTop: 4 }}>
                        <button onClick={() => setAll("reverb", true)} style={linkBtn}>all</button>
                        <span style={{ color: muted }}>/</span>
                        <button onClick={() => setAll("reverb", false)} style={linkBtn}>none</button>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const sel = selections[r.etsyListingId] || {};
                    const isOpen = !!expanded[r.etsyListingId];
                    const vars = r.variations || [];
                    return (
                      <React.Fragment key={r.etsyListingId}>
                      <tr>
                        <td style={S.td}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            {r.image ? (
                              <img src={r.image} alt="" style={{ width: 44, height: 44, objectFit: "cover", border: `1px solid ${ink}` }} />
                            ) : (
                              <div style={{ width: 44, height: 44, border: `1px solid ${ink}`, background: "rgba(10,10,15,0.05)" }} />
                            )}
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                                {ebayGap(r).warn && (
                                  <span title={`${ebayGap(r).missing.length} variant(s) missing from the eBay listing — expand to add`} style={{ color: amber, fontSize: 15, lineHeight: 1 }}>⚠️</span>
                                )}
                                {r.title || "Untitled"}
                              </div>
                              <div style={{ display: "flex", gap: 6, marginTop: 3, alignItems: "center", flexWrap: "wrap" }}>
                                {r.isNew && <span style={{ ...S.typeBadge, color: accent, borderColor: accent, fontSize: 10 }}>NEW · recommended</span>}
                                <span style={{ fontSize: 11, color: muted, fontFamily: mono }}>#{r.etsyListingId}</span>
                                {r.variationCount > 0 && (
                                  <button onClick={() => setExpanded((e) => ({ ...e, [r.etsyListingId]: !e[r.etsyListingId] }))} style={{ ...linkBtn, color: accent, fontWeight: 700 }}>
                                    {isOpen ? "▾" : "▸"} {r.variationCount} variants
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ ...S.td, fontFamily: mono, fontSize: 12 }} title={(r.skus || []).join(", ")}>{skuDisplay(r)}</td>
                        <td style={{ ...S.td, textAlign: "right", fontFamily: mono }}>{money(r.price, r.currency)}</td>
                        <td style={{ ...S.td, textAlign: "center" }}>
                          <PlatformCell status={r.ebayStatus} checked={!!sel.ebay} onChange={() => toggle(r.etsyListingId, "ebay")} platform="ebay" listingId={r.ebayListingId} />
                        </td>
                        <td style={{ ...S.td, textAlign: "center" }}>
                          <PlatformCell status={r.reverbStatus} checked={!!sel.reverb} onChange={() => toggle(r.etsyListingId, "reverb")} reverbSizes={r.variationCount} platform="reverb" listingId={r.reverbListingId} />
                        </td>
                      </tr>
                      {isOpen && vars.length > 0 && (
                        <tr>
                          <td colSpan={5} style={{ ...S.td, background: "rgba(0,51,255,0.04)", padding: "6px 16px 10px 70px" }}>
                            <div style={{ fontSize: 11, color: muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, fontFamily: mono }}>
                              {vars.length - (r.excludedVariations || []).length} of {vars.length} variants checked → that many Reverb listings · ✓ = already on that platform
                            </div>
                            {vars.map((v, i) => {
                              const included = !(r.excludedVariations || []).includes(v.label);
                              // Only claim "not on X" when we actually know that platform's variant set.
                              const tags = [];
                              if (v.listedEbay) tags.push(["✓ eBay", green]);
                              else if (r.ebayVariantsKnown && included) tags.push(["not on eBay", amber]);
                              if (v.listedReverb) tags.push(["✓ Reverb", green]);
                              else if (r.reverbVariantsKnown && included) tags.push(["not on Reverb", amber]);
                              return (
                                <label key={i} style={{ display: "flex", gap: 14, padding: "4px 0", fontSize: 13, alignItems: "center", borderTop: i ? `1px solid ${"rgba(10,10,15,0.08)"}` : "none", cursor: "pointer", opacity: included ? 1 : 0.45 }}>
                                  <input type="checkbox" checked={included} onChange={() => toggleVariation(r, v.label)} style={{ width: 16, height: 16, accentColor: accent }} />
                                  <span style={{ fontWeight: 600, minWidth: 110 }}>{v.label || "—"}</span>
                                  <span style={{ fontFamily: mono, color: v.sku ? ink : danger, minWidth: 150 }}>{v.sku || "(no SKU on Etsy)"}</span>
                                  <span style={{ fontFamily: mono }}>{money(v.price, r.currency)}</span>
                                  <span style={{ color: muted }}>qty {v.quantity ?? "—"}</span>
                                  <span style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
                                    {tags.map(([txt, col]) => <span key={txt} style={variantTag(col)}>{txt}</span>)}
                                    {tags.length === 0 && <span style={{ color: muted, fontSize: 11 }}>—</span>}
                                  </span>
                                </label>
                              );
                            })}
                            {ebayGap(r).warn && (() => {
                              const gap = ebayGap(r);
                              const listedEbayCount = (r.variations || []).filter((v) => v.listedEbay).length;
                              const singleExisting = listedEbayCount <= 1; // single-item listing → must end + relist
                              const busy = revising === r.etsyListingId;
                              const disabled = busy || gap.includedMissing.length === 0;
                              return (
                                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid rgba(10,10,15,0.08)`, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                                  <span style={{ fontSize: 12, color: ink }}>
                                    <span style={{ color: amber }}>⚠️</span>{" "}
                                    {gap.missing.length} variant(s) aren't on the eBay listing.{" "}
                                    {gap.includedMissing.length === 0
                                      ? "Check the missing variant above, then continue."
                                      : singleExisting
                                        ? "The current listing is a single item, so eBay can't add a variation — relist to combine them:"
                                        : "Add them to the existing listing:"}
                                  </span>
                                  <button
                                    onClick={() => reviseEbay(r.etsyListingId, singleExisting)}
                                    disabled={disabled}
                                    style={{ ...S.addBtn, fontSize: 12, padding: "7px 14px", opacity: disabled ? 0.5 : 1 }}
                                  >
                                    {busy
                                      ? (singleExisting ? "Relisting…" : "Updating…")
                                      : singleExisting
                                        ? `Relist on eBay as variation — ends current (+${gap.includedMissing.length})`
                                        : `Update eBay listing (+${gap.includedMissing.length})`}
                                  </button>
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      )}
                      {sel.ebay && (
                        <tr>
                          <td colSpan={5} style={{ ...S.td, background: "rgba(0,51,255,0.04)", padding: "8px 16px 12px 70px" }}>
                            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                              <span style={{ ...S.label, marginBottom: 0 }}>eBay category</span>
                              <div style={{ width: 360, maxWidth: "100%" }}>
                                <EbayCategoryPicker
                                  value={r.ebayCategoryId || ""}
                                  storeKey={storeKey}
                                  labels={ebayCatLabels}
                                  favs={ebayCatFavs}
                                  onToggleFav={toggleEbayCatFav}
                                  fallbackLabel={config.ebay_category_id ? `Default · #${config.ebay_category_id}` : ""}
                                  onPick={(id, path) => { rememberEbayCat(id, path); updateSource(r.etsyListingId, { ebayCategoryId: id }); }}
                                  onClear={() => updateSource(r.etsyListingId, { ebayCategoryId: "" })}
                                />
                              </div>
                              {r.variationCount > 1 && (
                                <EbayVariationName value={r.ebayVariationName} onChange={(name) => updateSource(r.etsyListingId, { ebayVariationName: name })} />
                              )}
                              <span style={{ fontSize: 12, color: muted }}>Set this before Build Drafts so eBay lists in the right category.</span>
                            </div>
                            {(r.ebayCategoryId || config.ebay_category_id) && (
                              <div style={{ marginTop: 10 }}>
                                <EbayAspects
                                  categoryId={r.ebayCategoryId || config.ebay_category_id || ""}
                                  storeKey={storeKey}
                                  value={r.ebayAspects || {}}
                                  onChange={(asp) => updateSource(r.etsyListingId, { ebayAspects: asp })}
                                  quantity={r.ebayQuantity}
                                  onQuantityChange={(q) => updateSource(r.etsyListingId, { ebayQuantity: q })}
                                />
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {loading && <div style={{ marginTop: 12, color: muted, fontSize: 13 }}>Loading…</div>}
        </>
      )}

      {tab === "review" && (
        <>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
            <button onClick={() => setShowSettings((v) => !v)} style={{ ...S.clearBtn, fontSize: 13 }}>{showSettings ? "Hide settings" : "⚙ Settings"}</button>
            {reverbTax.categories.length > 0 && (
              <>
                <div style={{ width: 260 }}>
                  <SearchableCategory value={bulkCat} cats={reverbTax.categories} onChange={setBulkCat} placeholder="— bulk Reverb category —" />
                </div>
                <button onClick={() => assignCategory(drafts.filter((d) => d.platform === "reverb" && !d.reverbCategoryUuid).map((d) => d.etsyListingId), bulkCat)} disabled={!bulkCat} style={{ ...S.clearBtn, fontSize: 13, opacity: bulkCat ? 1 : 0.5 }}>Apply to Reverb drafts missing one</button>
              </>
            )}
            <div style={{ flex: 1 }} />
            <button onClick={checkReverbStatus} disabled={checkingStatus} style={{ ...S.clearBtn, fontSize: 13, opacity: checkingStatus ? 0.6 : 1 }} title="Ask Reverb which drafts are now actually live">
              {checkingStatus ? "Checking…" : "Check Reverb status"}
            </button>
            <button onClick={() => publish(draftReady)} disabled={publishing || !draftReady.length} style={{ ...S.addBtn, opacity: (publishing || !draftReady.length) ? 0.5 : 1, cursor: (publishing || !draftReady.length) ? "not-allowed" : "pointer" }}>
              {publishing ? "Publishing…" : `Publish all drafts${draftReady.length ? ` (${draftReady.length})` : ""}`}
            </button>
          </div>

          {showSettings && (
            <div style={{ border: `2px solid ${ink}`, background: "#fff", padding: "16px 18px", marginBottom: 18 }}>
              <p style={{ fontSize: 13, color: muted, marginBottom: 16 }}>
                Defaults applied at publish. Condition + shipping are global; the Reverb category is set per listing on each card below. eBay needs Business Policies + an inventory location + a category. Saving requires CEO credentials.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
                {["eBay", "Reverb"].map((section) => (
                  <div key={section} style={{ marginBottom: 16 }}>
                    <div style={{ fontFamily: mono, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10, paddingBottom: 6, borderBottom: `2px solid ${ink}` }}>{section}</div>
                    {CONFIG_FIELDS.filter((f) => f.section === section).map((f) => {
                      const taxOptions = f.key === "reverb_condition_uuid" ? reverbTax.conditions
                        : f.key === "reverb_category_uuid" ? reverbTax.categories
                        : f.key === "reverb_shipping_profile_id" ? reverbTax.shippingProfiles.map((p) => ({ uuid: p.id, name: p.name }))
                        : f.key === "ebay_payment_policy_id" ? ebayAccount.paymentPolicies.map((p) => ({ uuid: p.id, name: p.name }))
                        : f.key === "ebay_return_policy_id" ? ebayAccount.returnPolicies.map((p) => ({ uuid: p.id, name: p.name }))
                        : f.key === "ebay_fulfillment_policy_id" ? ebayAccount.fulfillmentPolicies.map((p) => ({ uuid: p.id, name: p.name }))
                        : f.key === "ebay_location_key" ? ebayAccount.locations.map((p) => ({ uuid: p.id, name: p.name }))
                        : null;
                      const cur = config[f.key] || "";
                      const knownVal = taxOptions && taxOptions.some((o) => o.uuid === cur);
                      return (
                        <div key={f.key} style={{ marginBottom: 12 }}>
                          <label style={S.label}>{f.label}{f.hint ? <span style={{ textTransform: "none", color: muted, fontWeight: 400 }}> — {f.hint}</span> : null}</label>
                          {f.selectOptions ? (
                            <select value={cur || f.selectOptions[0].value} onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })} style={{ ...S.input, cursor: "pointer" }}>
                              {f.selectOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          ) : taxOptions && taxOptions.length ? (
                            <select value={cur} onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })} style={{ ...S.input, cursor: "pointer" }}>
                              <option value="">— select —</option>
                              {!knownVal && cur ? <option value={cur}>{cur} (saved)</option> : null}
                              {taxOptions.map((o) => <option key={o.uuid} value={o.uuid}>{o.name}</option>)}
                            </select>
                          ) : (
                            <input value={cur} onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })} style={S.input} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div style={{ borderTop: `2px solid ${ink}`, paddingTop: 14, display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div><label style={S.label}>CEO ID</label><input value={ceo.empId} onChange={(e) => setCeo({ ...ceo, empId: e.target.value })} style={{ ...S.input, width: 90 }} /></div>
                <div><label style={S.label}>Password</label><input type="password" value={ceo.password} onChange={(e) => setCeo({ ...ceo, password: e.target.value })} style={{ ...S.input, width: 180 }} /></div>
                <button onClick={saveConfig} disabled={savingCfg} style={{ ...S.addBtn, opacity: savingCfg ? 0.6 : 1 }}>{savingCfg ? "Saving…" : "Save Settings"}</button>
              </div>
            </div>
          )}

          {drafts.length === 0 ? (
            <div style={{ padding: "40px 20px", textAlign: "center", color: muted, border: `2px dashed ${ink}` }}>
              No drafts yet — pick listings on the Migrate tab and click “Build Drafts”.
            </div>
          ) : cols.map((col) => {
            const items = drafts.filter((d) => col.statuses.includes(d.status));
            if (!items.length) return null;
            return (
              <div key={col.key} style={{ marginBottom: 22 }}>
                <div style={{ fontFamily: mono, fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10, color: STATUS_META[col.statuses[0]]?.color || ink }}>
                  {col.label}{col.key === "published" ? "" : ` · ${items.length}`}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {items.map((d) => (
                    <div key={d.id} style={{ border: `2px solid ${ink}`, background: "#fff", padding: "12px 14px", display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                      {d.image ? (
                        <img src={d.image} alt="" style={{ width: 52, height: 52, objectFit: "cover", border: `1px solid ${ink}`, flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 52, height: 52, border: `1px solid ${ink}`, background: "rgba(10,10,15,0.05)", flexShrink: 0 }} />
                      )}
                      <div style={{ flex: "1 1 240px", minWidth: 200 }}>
                        {editingTitleId === d.id ? (
                          <input
                            autoFocus
                            value={titleDraft}
                            onChange={(e) => setTitleDraft(e.target.value)}
                            onBlur={() => { updateDraft(d.id, { titleOverride: titleDraft }); setEditingTitleId(null); }}
                            onKeyDown={(e) => { if (e.key === "Enter") { updateDraft(d.id, { titleOverride: titleDraft }); setEditingTitleId(null); } if (e.key === "Escape") setEditingTitleId(null); }}
                            style={{ ...S.input, fontSize: 15, fontWeight: 600, padding: "4px 8px" }}
                          />
                        ) : (
                          <div onClick={() => { setEditingTitleId(d.id); setTitleDraft(d.model || d.title || ""); }} title="Click to edit the listing title" style={{ fontWeight: 600, fontSize: 15, cursor: "text" }}>
                            {d.model || d.title || `#${d.etsyListingId}`}
                            <span style={{ marginLeft: 6, fontSize: 11, color: muted }}>✎</span>
                          </div>
                        )}
                        <div style={{ display: "flex", gap: 8, marginTop: 4, alignItems: "center", flexWrap: "wrap" }}>
                          <span style={{ ...S.typeBadge, fontSize: 10 }}>{d.platform}</span>
                          <StatusPill status={d.status} />
                          {d.sizeVariant && <span style={{ fontSize: 11, color: muted }}>Size: {d.sizeVariant}</span>}
                          {d.sku && <span style={{ ...S.typeBadge, fontSize: 10 }} title="SKU sent to the platform">SKU {d.sku}</span>}
                          {d.price ? <span style={{ fontSize: 11, color: muted, fontFamily: mono }}>{money(d.price)}</span> : null}
                        </div>
                        {d.error && <div style={{ fontSize: 12, color: danger, marginTop: 6 }}>{d.error}</div>}
                      </div>
                      {d.platform === "reverb" && d.status !== "published" && (
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", flex: "1 1 100%" }}>
                          {reverbTax.conditions.length > 0 && (
                            <div style={{ flex: "0 1 150px", minWidth: 130 }}>
                              <label style={{ ...S.label, marginBottom: 4 }}>Condition</label>
                              <select value={d.reverbConditionUuid || ""} onChange={(e) => updateSource(d.etsyListingId, { reverbConditionUuid: e.target.value })} style={{ ...S.input, fontSize: 12, padding: "6px 8px", cursor: "pointer" }}>
                                <option value="">— default —</option>
                                {reverbTax.conditions.map((o) => <option key={o.uuid} value={o.uuid}>{o.name}</option>)}
                              </select>
                            </div>
                          )}
                          <div style={{ flex: "0 1 170px", minWidth: 150 }}>
                            <label style={{ ...S.label, marginBottom: 4 }}>Quantity</label>
                            <select value={d.reverbUnique ? "unique" : "multiple"} onChange={(e) => updateSource(d.etsyListingId, { reverbUnique: e.target.value === "unique" })} style={{ ...S.input, fontSize: 12, padding: "6px 8px", cursor: "pointer" }}>
                              <option value="multiple">I have more than one</option>
                              <option value="unique">Unique item (sells once)</option>
                            </select>
                          </div>
                          {!d.reverbUnique && (
                            <div style={{ flex: "0 1 110px", minWidth: 90 }}>
                              <label style={{ ...S.label, marginBottom: 4 }}>Stock #</label>
                              <input type="number" min="0" value={d.reverbQuantity ?? ""} placeholder="default" onChange={(e) => updateSource(d.etsyListingId, { reverbQuantity: e.target.value })} style={{ ...S.input, fontSize: 12, padding: "6px 8px" }} />
                            </div>
                          )}
                          <div style={{ flex: "0 1 90px", minWidth: 80 }}>
                            <label style={{ ...S.label, marginBottom: 4 }}>Year</label>
                            <input value={d.reverbYear ?? ""} placeholder={config.reverb_year || "2024"} onChange={(e) => updateSource(d.etsyListingId, { reverbYear: e.target.value })} style={{ ...S.input, fontSize: 12, padding: "6px 8px" }} />
                          </div>
                          <div style={{ flex: "0 1 140px", minWidth: 110 }}>
                            <label style={{ ...S.label, marginBottom: 4 }}>Finish</label>
                            <input value={d.reverbFinish ?? ""} placeholder={config.reverb_finish || "Matte Black"} onChange={(e) => updateSource(d.etsyListingId, { reverbFinish: e.target.value })} style={{ ...S.input, fontSize: 12, padding: "6px 8px" }} />
                          </div>
                          {reverbTax.categories.length > 0 && (
                            <div style={{ flex: "1 1 220px", minWidth: 180 }}>
                              <label style={{ ...S.label, marginBottom: 4 }}>Reverb category</label>
                              <SearchableCategory value={d.reverbCategoryUuid || ""} cats={reverbTax.categories} onChange={(uuid) => assignCategory([d.etsyListingId], uuid)} invalid />
                            </div>
                          )}
                        </div>
                      )}
                      {d.platform === "ebay" && d.status !== "published" && (
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", flex: "1 1 100%" }}>
                          <div style={{ flex: "1 1 320px", minWidth: 240 }}>
                            <label style={{ ...S.label, marginBottom: 4 }}>eBay category</label>
                            <EbayCategoryPicker
                              value={d.ebayCategoryId || ""}
                              storeKey={storeKey}
                              labels={ebayCatLabels}
                              favs={ebayCatFavs}
                              onToggleFav={toggleEbayCatFav}
                              fallbackLabel={config.ebay_category_id ? `Default · #${config.ebay_category_id}` : ""}
                              onPick={(id, path) => { rememberEbayCat(id, path); updateSource(d.etsyListingId, { ebayCategoryId: id }); }}
                              onClear={() => updateSource(d.etsyListingId, { ebayCategoryId: "" })}
                            />
                          </div>
                          {d.variationCount > 1 && (
                            <EbayVariationName value={d.ebayVariationName} onChange={(name) => updateSource(d.etsyListingId, { ebayVariationName: name })} />
                          )}
                          {(d.ebayCategoryId || config.ebay_category_id) && (
                            <EbayAspects
                              categoryId={d.ebayCategoryId || config.ebay_category_id || ""}
                              storeKey={storeKey}
                              value={d.ebayAspects || {}}
                              onChange={(asp) => updateSource(d.etsyListingId, { ebayAspects: asp })}
                              quantity={d.ebayQuantity}
                              onQuantityChange={(q) => updateSource(d.etsyListingId, { ebayQuantity: q })}
                            />
                          )}
                        </div>
                      )}
                      {d.status !== "published" && <DimsRow d={d} onSet={updateSource} />}
                      <div style={{ display: "flex", gap: 12, alignItems: "center", flexShrink: 0 }}>
                        {d.status === "draft" && <button onClick={() => publish([d.id])} disabled={publishing} style={{ ...S.addBtn, fontSize: 12, padding: "7px 14px" }}>Publish</button>}
                        {d.status === "sent" && <button onClick={() => publish([d.id])} disabled={publishing} style={{ ...S.clearBtn, fontSize: 12, padding: "7px 14px" }}>Re-sync</button>}
                        {d.status === "failed" && <button onClick={() => rebuildOne(d)} disabled={building} style={{ ...S.addBtn, fontSize: 12, padding: "7px 14px" }}>Retry build</button>}
                        {d.status === "published" && d.targetListingId && /^\d+$/.test(String(d.targetListingId)) && (
                          <a href={d.platform === "reverb" ? `https://reverb.com/item/${d.targetListingId}` : `https://www.ebay.com/itm/${d.targetListingId}`} target="_blank" rel="noreferrer" style={{ ...linkBtn, color: green }}>view ↗</a>
                        )}
                        <button onClick={() => deleteDraft(d.id)} title="Remove draft" style={{ ...S.iconBtn, color: danger, borderColor: danger, padding: "4px 9px", fontSize: 14, fontWeight: 700, lineHeight: 1 }}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

const linkBtn = {
  background: "none", border: "none", padding: 0, cursor: "pointer",
  color: muted, fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
  textTransform: "uppercase", letterSpacing: "0.04em",
};

// Small per-variant status tag (listed on eBay/Reverb, or not listed)
const variantTag = (color) => ({
  fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.04em", color,
  border: `1px solid ${color}`, borderRadius: 2, padding: "1px 5px", whiteSpace: "nowrap",
});

function DimsRow({ d, onSet }) {
  const dm = d.dims || {};
  const set = (k, v) => onSet(d.etsyListingId, { dims: { ...dm, [k]: v } });
  const numStyle = { ...S.input, fontSize: 12, padding: "6px 6px", width: 48 };
  const selStyle = { ...S.input, fontSize: 12, padding: "6px 4px", width: 54, cursor: "pointer" };
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", flex: "1 1 100%", alignItems: "flex-end", marginTop: 4, paddingTop: 8, borderTop: `1px dashed ${muted}` }}>
      <div>
        <label style={{ ...S.label, marginBottom: 4 }}>Weight <span style={{ textTransform: "none", color: muted, fontWeight: 400 }}>· from Etsy</span></label>
        <div style={{ display: "flex", gap: 4 }}>
          <input type="number" min="0" value={dm.weight ?? ""} placeholder="—" onChange={(e) => set("weight", e.target.value)} style={numStyle} />
          <select value={dm.weight_unit || "oz"} onChange={(e) => set("weight_unit", e.target.value)} style={selStyle}>
            <option>oz</option><option>lb</option><option>g</option><option>kg</option>
          </select>
        </div>
      </div>
      <div>
        <label style={{ ...S.label, marginBottom: 4 }}>Dimensions (L × W × H)</label>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          {["length", "width", "height"].map((k, i) => (
            <React.Fragment key={k}>
              {i > 0 && <span style={{ color: muted }}>×</span>}
              <input type="number" min="0" value={dm[k] ?? ""} placeholder="—" onChange={(e) => set(k, e.target.value)} style={numStyle} />
            </React.Fragment>
          ))}
          <select value={dm.dim_unit || "in"} onChange={(e) => set("dim_unit", e.target.value)} style={selStyle}>
            <option>in</option><option>cm</option><option>mm</option><option>ft</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function SearchableCategory({ value, cats, onChange, placeholder = "— set category —", invalid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const selected = (cats || []).find((c) => c.uuid === value);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const ql = q.trim().toLowerCase();
  const filtered = (ql ? (cats || []).filter((c) => (c.name || "").toLowerCase().includes(ql)) : (cats || [])).slice(0, 300);
  const border = invalid && !value ? danger : ink;
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" onClick={() => { setOpen((o) => !o); setQ(""); }}
        style={{ ...S.input, borderColor: border, fontSize: 12, padding: "6px 8px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: selected ? ink : muted }}>{selected ? selected.name : (value || placeholder)}</span>
        <span style={{ color: muted }}>▾</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0, minWidth: 240, background: "#fff", border: `2px solid ${ink}`, zIndex: 60, maxHeight: 260, overflowY: "auto", boxShadow: `3px 3px 0 ${ink}` }}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search categories…"
            style={{ ...S.input, border: "none", borderBottom: `1px solid ${ink}`, fontSize: 12, position: "sticky", top: 0 }} />
          {value && <div onMouseDown={() => { onChange(""); setOpen(false); }} style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer", color: muted }}>— clear —</div>}
          {filtered.map((c) => (
            <div key={c.uuid} onMouseDown={() => { onChange(c.uuid); setOpen(false); }}
              style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer", background: c.uuid === value ? "rgba(0,51,255,0.08)" : "transparent" }}>{c.name}</div>
          ))}
          {filtered.length === 0 && <div style={{ padding: "8px 10px", fontSize: 12, color: muted }}>No matches</div>}
        </div>
      )}
    </div>
  );
}

// Server-backed picker over the ENTIRE eBay category tree (Commerce Taxonomy API).
// Too large to load client-side, so it debounce-searches the backend as you type.
function EbayCategoryPicker({ value, storeKey, labels, favs = [], onToggleFav, fallbackLabel, onPick, onClear }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setLoading(true); setErr("");
    const t = setTimeout(() => {
      fetch(`${API_URL}/api/crosslist/ebay-all-categories?store=${storeKey}&q=${encodeURIComponent(q)}`)
        .then((r) => r.json())
        .then((d) => { setResults(d.categories || []); if (d.error) setErr(d.error); })
        .catch(() => setErr("Couldn't reach eBay categories"))
        .finally(() => setLoading(false));
    }, 280);
    return () => clearTimeout(t);
  }, [q, open, storeKey]);

  const display = value ? (labels[value] || `#${value}`) : (fallbackLabel || "— use default —");
  const favIds = new Set((favs || []).map((f) => f.id));
  const ql = q.trim().toLowerCase();
  const favMatches = (favs || []).filter((f) => !ql || `${f.path || ""} ${f.name || ""}`.toLowerCase().includes(ql));
  const resultRows = (results || []).filter((c) => !favIds.has(c.id)); // favorites shown separately on top

  const renderRow = (c, keyPrefix) => (
    <div key={keyPrefix + c.id}
      style={{ display: "flex", alignItems: "stretch", borderBottom: "1px solid rgba(10,10,15,0.06)", background: c.id === value ? "rgba(0,51,255,0.08)" : "transparent" }}>
      <div onMouseDown={() => { onPick(c.id, c.path || c.name); setOpen(false); }}
        style={{ flex: 1, minWidth: 0, padding: "6px 10px", fontSize: 12, cursor: "pointer" }}>
        <div style={{ fontWeight: 600 }}>{c.name}</div>
        <div style={{ color: muted, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis" }}>{c.path || ""} · #{c.id}</div>
      </div>
      <button type="button" title={favIds.has(c.id) ? "Remove favorite" : "Add to favorites"}
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (onToggleFav) onToggleFav(c); }}
        style={{ border: "none", background: "none", cursor: "pointer", padding: "0 10px", fontSize: 16, lineHeight: 1, color: favIds.has(c.id) ? "#f5a623" : muted, alignSelf: "center" }}>
        {favIds.has(c.id) ? "★" : "☆"}
      </button>
    </div>
  );
  const sectionHeader = (label) => (
    <div style={{ padding: "5px 10px", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: muted, fontFamily: mono, background: "rgba(10,10,15,0.04)", position: "sticky", top: 34, zIndex: 1 }}>{label}</div>
  );
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" onClick={() => { setOpen((o) => !o); setQ(""); }}
        style={{ ...S.input, borderColor: value ? ink : muted, fontSize: 12, padding: "6px 8px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: value ? ink : muted }}>{display}</span>
        <span style={{ color: muted }}>▾</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0, minWidth: 300, background: "#fff", border: `2px solid ${ink}`, zIndex: 60, maxHeight: 320, overflowY: "auto", boxShadow: `3px 3px 0 ${ink}` }}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all eBay categories…"
            style={{ ...S.input, border: "none", borderBottom: `1px solid ${ink}`, fontSize: 12, position: "sticky", top: 0, zIndex: 2 }} />
          {value && <div onMouseDown={() => { onClear(); setOpen(false); }} style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer", color: muted }}>— use Settings default —</div>}
          {favMatches.length > 0 && sectionHeader("★ Favorites")}
          {favMatches.map((c) => renderRow(c, "f"))}
          {favMatches.length > 0 && sectionHeader("All categories")}
          {loading && <div style={{ padding: "8px 10px", fontSize: 12, color: muted }}>Searching…</div>}
          {err && <div style={{ padding: "8px 10px", fontSize: 12, color: danger }}>{err}</div>}
          {!loading && !err && resultRows.map((c) => renderRow(c, "r"))}
          {!loading && !err && resultRows.length === 0 && favMatches.length === 0 && (
            <div style={{ padding: "8px 10px", fontSize: 12, color: muted }}>{q ? "No matches" : "Type to search, or star categories to save them here"}</div>
          )}
        </div>
      )}
    </div>
  );
}

// Category-required eBay item specifics (aspects). Loads the required fields for the
// chosen category so listings don't fail Verify with "item specific X is missing".
function EbayAspects({ categoryId, storeKey, value = {}, onChange, quantity, onQuantityChange }) {
  const [aspects, setAspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [showOptional, setShowOptional] = useState(false);

  useEffect(() => {
    if (!categoryId) { setAspects([]); return undefined; }
    let cancel = false;
    setLoading(true); setErr("");
    fetch(`${API_URL}/api/crosslist/ebay-category-aspects?store=${storeKey}&categoryId=${encodeURIComponent(categoryId)}`)
      .then((r) => r.json())
      .then((d) => { if (!cancel) { setAspects(d.aspects || []); if (d.error) setErr(d.error); } })
      .catch(() => { if (!cancel) setErr("Couldn't load eBay required fields"); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [categoryId, storeKey]);

  if (!categoryId) return null;
  const required = aspects.filter((a) => a.required);
  const optional = aspects.filter((a) => !a.required);
  const set = (name, v) => onChange({ ...value, [name]: v });
  // Defaults applied on the listing server-side too, so these are guaranteed even if untouched.
  const ASPECT_DEFAULTS = { Brand: "Vibrato Vault", Type: "Other" };

  const field = (a) => {
    const listId = `asp-${categoryId}-${a.name}`.replace(/[^a-zA-Z0-9_-]/g, "-");
    const dflt = ASPECT_DEFAULTS[a.name] || "";
    const shown = value[a.name] || dflt;
    const missing = a.required && !shown.trim();
    return (
      <div key={a.name} style={{ flex: "0 1 210px", minWidth: 160 }}>
        <label style={{ ...S.label, marginBottom: 4 }}>{a.name}{a.required ? " *" : ""}</label>
        <input
          list={a.values && a.values.length ? listId : undefined}
          value={shown}
          onChange={(e) => set(a.name, e.target.value)}
          placeholder={a.required ? "required" : "optional"}
          style={{ ...S.input, fontSize: 12, padding: "6px 8px", borderColor: missing ? danger : ink }}
        />
        {a.values && a.values.length > 0 && (
          <datalist id={listId}>{a.values.map((v) => <option key={v} value={v} />)}</datalist>
        )}
      </div>
    );
  };

  const qtyField = (
    <div style={{ flex: "0 1 110px", minWidth: 90 }}>
      <label style={{ ...S.label, marginBottom: 4 }}>eBay qty *</label>
      <input
        type="number" min="0"
        value={quantity === "" || quantity == null ? 5 : quantity}
        onChange={(e) => onQuantityChange && onQuantityChange(e.target.value)}
        title="Units to list on eBay (default 5). Kept low so listings don't blow past eBay selling limits."
        style={{ ...S.input, fontSize: 12, padding: "6px 8px" }}
      />
    </div>
  );

  return (
    <div style={{ flex: "1 1 100%", marginTop: 4 }}>
      {loading && <div style={{ fontSize: 12, color: muted }}>Loading eBay required fields…</div>}
      {err && <div style={{ fontSize: 12, color: danger }}>{err}</div>}
      {!loading && !err && (
        <>
          <div style={{ ...S.label, marginBottom: 6 }}>eBay item specifics{required.length > 0 ? " (required)" : ""}</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>{qtyField}{required.map(field)}</div>
        </>
      )}
      {!loading && !err && optional.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <button type="button" onClick={() => setShowOptional((v) => !v)} style={{ ...linkBtn, color: accent, fontWeight: 700 }}>
            {showOptional ? "▾" : "▸"} {optional.length} optional item specifics
          </button>
          {showOptional && <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>{optional.map(field)}</div>}
        </div>
      )}
    </div>
  );
}

function PlatformCell({ status, checked, onChange, reverbSizes, platform, listingId }) {
  if (["published", "sent", "draft", "creating", "listed"].includes(status)) {
    // A completed listing reads the same whether we published it or detected it
    // already live — both show "Listed" for consistency across uploads.
    const dispStatus = (status === "published") ? "listed" : status;
    const pill = <StatusPill status={dispStatus} />;
    if (["listed", "published"].includes(status) && listingId && /^\d+$/.test(String(listingId))) {
      const href = platform === "reverb" ? `https://reverb.com/item/${listingId}` : `https://www.ebay.com/itm/${listingId}`;
      return <a href={href} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }} title="Already listed — open it">{pill}</a>;
    }
    return pill;
  }
  return (
    <label style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 2, cursor: "pointer" }}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ width: 18, height: 18, accentColor: accent, cursor: "pointer" }} />
      {status === "failed" && <span style={{ fontSize: 9, color: danger, fontFamily: mono }}>retry</span>}
      {reverbSizes > 1 && <span style={{ fontSize: 9, color: muted }}>→ {reverbSizes} listings</span>}
    </label>
  );
}
